import React from 'react';
import PropTypes from 'prop-types';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { useTheme } from 'contexts/ThemeContext';

import { Text, Tooltip } from 'components/DataDisplay';
import Image from 'components/Image';
import { AutocompleteTextField, IconButton } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { TableData } from 'components/TableData';

export const TableConnectorComponent = props => {
  const {
    isCampaignSetupPending,
    mediaLogos,
    formattedPlatforms,
    displayInput,
    handleChangeExternalId,
    allPlatformAccounts,
    allPlatformPages,
    campaign,
    activationShopGroups,
    blockedPagesMedias,
    domainAccounts = [],
    getMediaModelByMedia,
    domainPages = [],
    activableMedias
  } = props;

  const { themeColors } = useTheme();

  const headerTextProps = {
    fontSize: '15px',
    fontWeight: 600,
    style: { letterSpacing: '0.07em' }
  };

  const cellStyle = {
    paddingTop: '12px',
    paddingBottom: '12px',
    borderBottom: `1px solid ${themeColors.designSystem.line}`
  };

  const formatAccount = (setup, row) => {
    if (row.media === 'sms') {
      return (
        <Text
          fontSize="12px"
          fontWeight={400}
          style={{ textAlign: 'left' }}
          color={themeColors.themeV2.black}
        >
          {setup?.name
            ? `${setup.name} (${setup.connexionId})`
            : setup.connexionId}
        </Text>
      );
    }
    if (setup?.connexionId) {
      return (
        <Text
          fontSize="12px"
          fontWeight={400}
          style={{ textAlign: 'left' }}
          color={themeColors.themeV2.black}
        >
          {setup.connexionId}
        </Text>
      );
    }
    return (
      <Text
        fontSize="12px"
        fontWeight={400}
        style={{ textAlign: 'left' }}
        color={themeColors.themeV2.black}
      >
        {`${setup.name} (${setup.config?.externalId})`}
      </Text>
    );
  };

  const isObject = data => {
    return data !== null && typeof data === 'object';
  };

  const getTargetModel = (media, value) =>
    (typeof getMediaModelByMedia === 'function'
      ? getMediaModelByMedia(media)
      : undefined) ||
    value?.config?.model ||
    value?.model ||
    value?.media;

  const uniqueByKey = arr => {
    const seen = new Set();
    return (arr || []).filter(o => {
      const k = String(o?.key ?? '');
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };

  const mapProjectAccount = el => ({
    key: el?.config?.externalId
      ? el.config.externalId
      : el?.config?.externalId?.externalId,
    label: `${el?.groupName} - ${el?.name}`,
    group: el?.groupName || 'ermesCSM',
    config: el?.config
  });

  const buildNonSmsAccountOptions = ({
    value,
    mediaAccounts = [],
    domainAccountsLocal = []
  }) => {
    const targetModel = getTargetModel(value?.media, value);

    const domainOptions = (domainAccountsLocal || [])
      .filter(acc => acc?.model === targetModel)
      .map(acc => ({
        key: acc?.config?.externalId || '',
        label: `${acc?.name || acc?.model} — ${acc?.config?.externalId || ''}`,
        group: 'Domaine',
        config: { connexionId: acc?.config?.connexionId || null }
      }));

    const projectOptions = (mediaAccounts || []).map(mapProjectAccount);

    return uniqueByKey([...domainOptions, ...projectOptions]);
  };

  const buildNonSmsPageOptions = ({
    value,
    mediaPages,
    domainPagesLocal = []
  }) => {
    const targetModel = getTargetModel(value?.media, value);

    const projectPages = (mediaPages || []).map(el => ({
      key: el?.config?.externalId ?? el?.config?.externalId?.externalId ?? '',
      label: `${el?.groupName} - ${el?.name}`,
      group: el?.groupName || 'ermesCSM'
    }));

    const domainPagesMapped = (domainPagesLocal || [])
      .filter(
        p =>
          (p?.model && p.model === targetModel) ||
          (p?.media && p.media === value?.media) ||
          !p?.model
      )
      .map(p => ({
        key: p?.config?.externalId ?? p?.config?.externalId?.externalId ?? '',
        label: `${p?.name || p?.model || 'Page Domaine'} — ${p?.config
          ?.externalId ?? ''}`,
        group: 'Domaine'
      }));

    return uniqueByKey([...projectPages, ...domainPagesMapped]);
  };

  const buildSmsOptions = ({
    type,
    domainAccountsLocal = [],
    mediaAccountsSMS = [],
    mediaPages = [],
    domainPagesLocal = [],
    validatedAllPlatformAccounts
  }) => {
    const smsDomainOptions = (domainAccountsLocal || [])
      .filter(acc =>
        ['sms', 'spothit', 'spothit'].includes(
          String(acc?.model || '').toLowerCase()
        )
      )
      .map(acc => ({
        key: acc?.config?.connexionId || '',
        label: `${acc?.name || acc?.model} — ${acc?.config?.connexionId || ''}`,
        group: 'Domaine'
      }));

    if (type === 'account') {
      const projectOptions =
        typeof validatedAllPlatformAccounts === 'function'
          ? validatedAllPlatformAccounts(mediaAccountsSMS)
          : mediaAccountsSMS.map(el => {
              const key =
                el?.config?.connexionId ?? el?.config?.config?.connexionId;
              return {
                key,
                label: `${el?.groupName} - ${el?.name}`,
                group: el?.groupName || 'ermesCSM'
              };
            });

      return uniqueByKey([...smsDomainOptions, ...projectOptions]);
    }

    const campaignPages = (mediaPages || []).map(el => ({
      key: el?.config?.connexionId ?? el?.config?.config?.connexionId,
      label: `${el?.groupName} - ${el?.name}`,
      group: el?.groupName || 'ermesCSM'
    }));

    const domainSmsPages = (domainPagesLocal || []).map(p => ({
      key: p?.config?.connexionId ?? p?.config?.config?.connexionId ?? '',
      label: `${p?.name || p?.model || 'Page Domaine'} — ${p?.config
        ?.connexionId ?? ''}`,
      group: 'Domaine'
    }));

    return uniqueByKey([...campaignPages, ...domainSmsPages]);
  };

  const formatItem = ({ value, row, type, disabled }) => {
    if (value === null || disabled) {
      return <Text fontSize="12px" fontWeight={700} />;
    }

    const validatedAllPlatformAccounts = allPlatformAccountsTest => {
      const returnValue = allPlatformAccountsTest.reduce(
        (acc, currentValue) => {
          const key = currentValue?.config?.connexionId
            ? currentValue.config.connexionId
            : currentValue.config?.config?.connexionId;
          if (key) {
            acc.push({
              key,
              label: `${currentValue.groupName} - ${currentValue.name}`,
              group: currentValue.groupName || 'ermesCSM'
            });
          }
          return acc;
        },
        []
      );
      return returnValue;
    };

    if (row.drawer) {
      const group = activationShopGroups.filter(el =>
        el.shops?.includes(campaign.activationShopId)
      );

      const allAccountNames = new Set([campaign.groupName]);
      group.forEach(el => {
        allAccountNames.add(el?.name);
        allAccountNames.add(el?.parent);
        allAccountNames.add(
          activationShopGroups.find(shop => shop.key === el?.parent)?.name
        );
      });

      const modelMatches = el =>
        el.model === value?.config?.model ||
        el.model === value?.model ||
        el.model === value?.media ||
        el.model === value?.config?.media;

      let mediaAccounts = allPlatformAccounts.filter(
        el => allAccountNames.has(el.groupName) && modelMatches(el)
      );

      if (mediaAccounts.length === 0) {
        mediaAccounts = allPlatformAccounts.filter(el => modelMatches(el));
      }

      let mediaPages = allPlatformPages.filter(
        el => allAccountNames.has(el.groupName) && el.media === value?.media
      );

      if (mediaPages.length === 0) {
        mediaPages = allPlatformPages.filter(el => el.media === value?.media);
      }

      const mediaAccountsSMS = allPlatformAccounts.filter(
        el =>
          (allAccountNames.has(el.groupName) && modelMatches(el)) ||
          el.model === 'spotHit' ||
          el.media === 'spotHit'
      );

      if (row.media === 'sms') {
        const smsOptions = buildSmsOptions({
          type,
          domainAccountsLocal: domainAccounts, // fix typo
          mediaAccountsSMS,
          mediaPages,
          domainPagesLocal: domainPages, // << passer les domainPages
          validatedAllPlatformAccounts
        });

        const defaultSmsKey = value?.config?.connexionId || '';
        const defaultSmsLabel =
          smsOptions.find(o => o.key === defaultSmsKey)?.label || defaultSmsKey;

        return (
          <Container flex>
            <Item justify="flex-start">
              <Row style={{ flexFlow: 'row', margin: '0px' }}>
                <AutocompleteTextField
                  options={smsOptions}
                  formatLabel={option => `${option.label} (${option.key})`}
                  defaultValue={{ key: defaultSmsKey, label: defaultSmsLabel }}
                  onChange={handleChangeExternalId(type, value.media)}
                />
              </Row>
            </Item>
          </Container>
        );
      }

      const isDisabled =
        type === 'page' && blockedPagesMedias.includes(value?.media);

      const autocompleteField = (
        <AutocompleteTextField
          options={
            type === 'account'
              ? buildNonSmsAccountOptions({
                  value,
                  mediaAccounts,
                  domainAccountsLocal: domainAccounts
                })
              : buildNonSmsPageOptions({
                  value,
                  mediaPages,
                  domainPagesLocal: domainPages
                })
          }
          formatLabel={option => `${option.label} (${option.key})`}
          defaultValue={{
            key:
              value?.config?.config?.externalId ??
              value?.config?.externalId ??
              '',
            label:
              value?.config?.name ||
              value?.config?.config?.externalId ||
              value?.config?.externalId ||
              '',
            group: value?.config?.groupName || 'ermesCSM',
            config: value?.config
          }}
          onChange={handleChangeExternalId(type, value.media)}
          disabled={isDisabled}
        />
      );

      return (
        <Container flex>
          <Item justify="flex-start">
            <Row style={{ flexFlow: 'row', margin: '0px' }}>
              {isDisabled ? (
                <Tooltip
                  title="Un post existant provient de cette page, vous devez donc réaliser le setup auto sur cette page."
                  style={{ width: '150px' }}
                >
                  {autocompleteField}
                </Tooltip>
              ) : (
                autocompleteField
              )}
            </Row>
          </Item>
        </Container>
      );
    }
    if (isObject(value?.config)) {
      if (value.media === 'sms') {
        return (
          <Container flex>
            <Item justify="flex-start">{formatAccount(value.config, row)}</Item>
          </Container>
        );
      }
      if (isObject(value?.config?.config)) {
        return (
          <Container flex>
            <Item justify="flex-start">{formatAccount(value.config, row)}</Item>
          </Container>
        );
      }
    }
    if (value?.config === 'ermes Account') {
      return (
        <Container flex>
          <Item justify="flex-start">
            <Text
              fontSize="12px"
              fontWeight={400}
              color={themeColors.themeV2.black}
            >
              Compte Ermes
            </Text>
          </Item>
        </Container>
      );
    }
    return (
      <Container flex>
        <Item justify="flex-start">
          <Text
            fontSize="12px"
            fontWeight={400}
            italic
            color={
              type === 'account' ? themeColors.red : themeColors.themeV2.black
            }
          >
            Pas de compte défini
          </Text>
        </Item>
      </Container>
    );
  };

  return (
    <Item flex style={{ margin: '20px 10px 0px 10px', width: '100%' }}>
      <TableData
        style={{ borderRadius: '0px' }}
        noPaginate
        noBorderRadius
        noBorderColor
        noBoxShadow
        headerJustify="flex-start"
        sizeOfcolumnAction={{ width: '0px' }}
        headers={[
          {
            id: 'media',
            cellStyle,
            label: (
              <Text {...headerTextProps} color={themeColors.designSystem.muted}>
                MEDIA
              </Text>
            ),
            format: value => {
              const mediaLogo =
                mediaLogos.find(
                  el => el.key.toLowerCase() === value.toLowerCase()
                )?.base64Image || '';
              return (
                <Container flex>
                  <Item
                    justify="flex-start"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    {mediaLogo ? (
                      <Image src={mediaLogo} alt={value} height="38px" />
                    ) : (
                      <Text
                        fontSize="13px"
                        fontWeight={600}
                        color={themeColors.designSystem.muted}
                      >
                        {value}
                      </Text>
                    )}
                    <Text
                      fontSize="13px"
                      fontWeight={600}
                      color={themeColors.designSystem.ink}
                    >
                      {value.charAt(0).toUpperCase() + value.slice(1)}
                    </Text>
                  </Item>
                </Container>
              );
            }
          },
          {
            id: 'account',
            cellStyle,
            label: (
              <Text {...headerTextProps} color={themeColors.designSystem.muted}>
                COMPTE PUBLICITAIRE
              </Text>
            ),
            format: (value, row) => {
              return formatItem({ value, row, type: 'account' });
            }
          },
          {
            id: 'page',
            cellStyle,
            label: (
              <Text {...headerTextProps} color={themeColors.designSystem.muted}>
                PAGE
              </Text>
            ),
            format: (value, row) => {
              const activableMedia = activableMedias?.find(
                el => el.key === row.media
              );
              return formatItem({
                value,
                row,
                type: 'page',
                disabled: !activableMedia?.page
              });
            }
          },
          {
            id: '',
            cellStyle,
            label: '',
            format: (value, row) => {
              return (
                <Container>
                  <Row spacing={0}>
                    <Item justify="flex-end">
                      <IconButton
                        disabled={isCampaignSetupPending}
                        onClick={() => {
                          displayInput(row._id, row.campaignId);
                        }}
                      >
                        {row.drawer ? (
                          <CheckIcon style={{ color: themeColors.light }} />
                        ) : (
                          <EditOutlinedIcon
                            style={{ color: themeColors.designSystem.faint }}
                          />
                        )}
                      </IconButton>
                    </Item>
                  </Row>
                </Container>
              );
            }
          }
        ]}
        rows={formattedPlatforms}
      />
    </Item>
  );
};

TableConnectorComponent.defaultProps = {
  isCampaignSetupPending: false,
  mediaLogos: [],
  formattedPlatforms: [],
  displayInput: () => {},
  handleChangeExternalId: () => {}
};

TableConnectorComponent.propTypes = {
  isCampaignSetupPending: PropTypes.bool,
  mediaLogos: PropTypes.arrayOf(),
  formattedPlatforms: PropTypes.arrayOf(),
  displayInput: PropTypes.func,
  handleChangeExternalId: PropTypes.func
};

export default TableConnectorComponent;
